// @ts-ignore
import { saveAs } from "file-saver";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnDestroy,
  OnInit
} from "@angular/core";

import { views } from "@onsip/web/app/phone/views";
import { BehaviorSubject, combineLatest, Subscription } from "rxjs";
import { BreakpointObserver } from "@angular/cdk/layout";
import {
  UserAlias,
  UserAliasService
} from "@onsip/common/services/api/resources/userAlias/user-alias.service";
import { Did, DidService } from "@onsip/common/services/api/resources/did/did.service";
import {
  AddressSummary,
  AddressSummaryService
} from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
import { AllAddressType } from "@onsip/common/services/api/resources/addressSummary/address-summary";
import { AdminTableAbstractComponent } from "@onsip/web/features/abstractClass/admin-table-abstract.component";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FormatE164Pipe } from "@onsip/web/features/shared/pipes/format-e164.pipe";
import { FormatExtensionsPipe } from "@onsip/web/features/shared/pipes/format-extensions.pipe";
import {
  DownloadDirectoryModalComponent,
  FileType
} from "@onsip/web/features/administrators/directory/downloadDirectoryModal/download-directory-modal.component";

interface Directory {
  sipAddress: string;
  name: string;
  extensions: Array<string>;
  phoneNumbers: Array<string>;
  addressType: string;
  link: string;
}
interface AddressType {
  title: string;
  link: string;
}
const addressTypesMap = new Map<AllAddressType, AddressType>([
  ["group", { title: "Group", link: views.GROUPS }],
  ["adv_attendant_menu", { title: "Attendant menu", link: views.ADMIN_ATTENDANT_MENU }],
  ["inbound_bridge", { title: "Inbound bridge", link: views.ADMIN_INBOUND_BRIDGE }],
  ["adv_announcement", { title: "Announcement", link: views.ADMIN_ADV_ANNOUNCEMENTS }],
  ["adv_moh", { title: "Music on hold", link: views.ADMIN_MUSIC_ON_HOLD }],
  ["user", { title: "Users", link: views.USERS }],
  ["adv_voicemailbox", { title: "Voicemail", link: views.ADMIN_VOICEMAIL }],
  ["adv_dbn_directory", { title: "Dial by name directory", link: views.ADMIN_DBN_DIRECTORY }],
  ["adv_park", { title: "Parking lot", link: views.ADMIN_PARKING_LOT }],
  ["adv_park_retriever", { title: "Parking lot retrieval", link: views.ADMIN_PARKING_LOT }],
  ["time_switch", { title: "Business hour rule", link: views.ADMIN_BHR }],
  ["adv_queue", { title: "ACD queue", link: views.ADMIN_ACD_QUEUES }],
  ["adv_queue_login", { title: "ACD queue login", link: views.ADMIN_ACD_QUEUES }],
  ["e911", { title: "911 Emergency", link: views.E911_TABLE }],
  [
    "recording_management",
    { title: "Recording manager", link: views.ADMIN_PBX_SETTINGS_RECORDING_MANAGER }
  ],
  ["telephone_number", { title: "External phone number", link: views.ADMIN_EXTERNAL_PHONE_NUMBER }],
  [
    "voicemail_management",
    { title: "Voicemail management", link: views.ADMIN_PBX_SETTINGS_VOICEMAIL_MANAGER }
  ],
  ["external", { title: "External", link: views.ADMIN_EXTERNAL_ADDRESS }],
  ["contact_center", { title: "Contact center", link: "" }],
  ["conference_bridge", { title: "Conference bridge", link: views.ADMIN_CONFERENCE_SUITE }]
]);

@Component({
  selector: "onsip-directories-page",
  templateUrl: "./directories-page.component.html",
  styleUrls: ["./directories-page.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FormatE164Pipe, FormatExtensionsPipe],
  standalone: false
})
export class DirectoriesPageComponent
  extends AdminTableAbstractComponent<Directory>
  implements OnInit, OnDestroy
{
  @HostBinding("class.onsip-admin-host-grid")
  @HostBinding("class.onsip-grid-content")
  _dontUse = true;
  hasDestinations = new BehaviorSubject<boolean>(true);
  defaultSortingColumn = "name";

  protected displayedColumnsRecord = {
    XSmall: ["sipAddress"],
    Small: ["sipAddress", "addressType"],
    Medium: ["sipAddress", "name", "addressType"],
    Large: ["sipAddress", "name", "extensions", "phoneNumbers", "addressType"],
    XLarge: ["sipAddress", "name", "extensions", "phoneNumbers", "addressType"]
  };

  columnsName = new Map([
    ["sipAddress", this.translate.instant("ONSIP_I18N.SIP_ADDRESS")],
    ["name", this.translate.instant("ONSIP_I18N.NAME")],
    ["extensions", this.translate.instant("ONSIP_I18N.EXTENSIONS")],
    ["phoneNumbers", this.translate.instant("ONSIP_I18N.PHONE_NUMBERS")],
    ["addressType", this.translate.instant("ONSIP_I18N.ADDRESS_TYPE")]
  ]);
  protected unsubscriber = new Subscription();
  constructor(
    private userAliasService: UserAliasService,
    private didService: DidService,
    private addressSummaryService: AddressSummaryService,
    protected dialog: MatDialog,
    protected translate: TranslateService,
    protected cdr: ChangeDetectorRef,
    protected breakpointObserver: BreakpointObserver,
    private formatE164Pipe: FormatE164Pipe,
    private formatExtensionsPipe: FormatExtensionsPipe
  ) {
    super(breakpointObserver, cdr, dialog, translate);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setDataFilter();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.unsubscriber.unsubscribe();
  }

  protected getItemValueByColumn(item: Directory, columnName: string): string | number {
    switch (columnName) {
      case "sipAddress":
        return item.sipAddress.toLowerCase();
      case "name":
        return item.name.toLowerCase();
      case "extensions":
        return item.extensions?.join(",").toLowerCase();
      case "phoneNumbers":
        return item.phoneNumbers?.join(",").toLowerCase();
      case "addressType":
        return item.addressType?.toLowerCase();
      default:
        return item.sipAddress.toLowerCase();
    }
  }

  protected initDataSource(): void {
    this.browseData();
    this.getDataFromState();
  }

  private browseData(): void {
    this.userAliasService.userAliasBrowse();
    this.didService.didBrowse();
    this.addressSummaryService.addressSummaryBrowse();
  }

  protected getItemName(): string {
    return "";
  }

  protected performDeleteRequest(): void {}

  private getDataFromState(): void {
    this.unsubscriber.add(
      combineLatest({
        userAliases: this.userAliasService.orgState,
        dids: this.didService.orgState,
        addressSummaries: this.addressSummaryService.orgState
      }).subscribe(({ userAliases, dids, addressSummaries }) => {
        const addresses = addressSummaries.filter(
          directory => directory.addressType !== "time_switch"
        );
        const bhrAddresses = this.bhrSeparator(
          addressSummaries.filter(directory => directory.addressType === "time_switch")
        );
        const addressesArray = [...bhrAddresses, ...addresses];
        this.dataSource.data = this.getAddressSummaries(addressesArray, userAliases, dids);

        this.isDataObtained$.next(true);
      })
    );
  }

  private bhrSeparator(addressSummaries: Array<AddressSummary>): Array<AddressSummary> {
    const reg = new RegExp(/__bhr_[a-zA-Z][_a-zA-Z0-9-.]*(\\.[_a-zA-Z0-9-.]+)*([a-zA-Z0-9])_[1-9]/);
    return addressSummaries.filter(rule => !rule?.username.match(reg));
  }

  private getAddressType(addressType: AllAddressType): AddressType | undefined {
    return addressTypesMap.get(addressType);
  }

  private sortedPdfCsvData(): Array<Directory> {
    return this.dataSource.data.sort((a, b) => {
      if (a.name.toLowerCase() < b.name.toLowerCase()) {
        return -1;
      }
      if (a.name.toLowerCase() > b.name.toLowerCase()) {
        return 1;
      }
      return 0;
    });
  }

  private getAddressSummaries(
    addressSummaries: Array<AddressSummary>,
    userAliases: Array<UserAlias>,
    dids: Array<Did>
  ): Array<Directory> {
    return addressSummaries.map(addressSummary => ({
      sipAddress: addressSummary.aor || " ",
      name: addressSummary.name || " ",
      extensions: this.getExtensions(userAliases, addressSummary),
      phoneNumbers: this.getPhoneNumbers(dids, addressSummary),
      addressType: this.getAddressType(addressSummary.addressType)?.title || " ",
      link: this.getAddressType(addressSummary.addressType)?.link || " "
    }));
  }

  private generatePDFdata(): Array<Record<string, any>> {
    return this.sortedPdfCsvData().map((directory: Record<string, any>) => {
      const tempData = { ...directory };
      tempData.extensions = tempData.extensions
        .map((extension: string) => {
          return this.formatExtensionsPipe.transform(extension);
        })
        .join(", ");
      tempData.phoneNumbers = tempData.phoneNumbers
        .map((phoneNumber: string) => {
          return this.formatE164Pipe.transform(phoneNumber);
        })
        .join(", ");
      return tempData;
    });
  }

  generatePDF(): void {
    const printableDoc = new jsPDF("landscape");
    const displayCols = ["sipAddress", "name", "extensions", "phoneNumbers", "addressType"];
    autoTable(printableDoc, {
      body: this.generatePDFdata(),
      theme: "grid",
      columns: displayCols.map(col => ({ header: this.columnsName.get(col), dataKey: col })),
      headStyles: {
        fillColor: "#fff",
        textColor: "#000",
        lineWidth: 0.1
      },
      columnStyles: {
        sipAddress: { cellWidth: 80 },
        name: { cellWidth: "auto" },
        extensions: { cellWidth: 40 },
        phoneNumbers: { cellWidth: 60 },
        addressType: { cellWidth: 50 }
      },

      didDrawPage() {
        printableDoc.setFontSize(10);
      }
    });
    printableDoc.save("onsip-directory.pdf");
  }

  private downloadCSV(): void {
    let rowsCsv = "";
    const items = this.sortedPdfCsvData().map(el => {
      const csvRow = Object.assign(
        {},
        {
          sipAddress: el.sipAddress,
          name: el.name,
          extensions: [...el.extensions],
          phoneNumbers: [...el.phoneNumbers],
          addressType: el.addressType
        }
      );
      return csvRow;
    }) as Array<Directory>;
    for (let i = 1; i < items.length; i++) {
      rowsCsv += this.processRow(items[i], i, items) + "\r\n";
    }

    const headerRow: string = [
      this.translate.instant("ONSIP_I18N.SIP_ADDRESS"),
      this.translate.instant("ONSIP_I18N.NAME"),
      this.translate.instant("ONSIP_I18N.EXTENSIONS"),
      this.translate.instant("ONSIP_I18N.PHONE_NUMBERS"),
      this.translate.instant("ONSIP_I18N.ADDRESS_TYPE")
    ].join(",");
    const csvString: string = headerRow + "\r\n" + rowsCsv;
    this.saveAsCsv(csvString);
  }

  private processRow(row: Directory, idx: number, items: Array<Record<string, any>>): string {
    return Object.keys(row)
      .map((val: string) => {
        if (items[idx][val] instanceof Array) {
          return `${(items[idx][val] = items[idx][val]
            .map((entityName: string) => {
              if (val === "extensions") {
                return this.formatExtensionsPipe.transform(entityName);
              }
              if (val === "phoneNumbers") {
                return this.formatE164Pipe.transform(entityName);
              }
            })
            .join("; "))}`;
        } else {
          return (items[idx][val] = items[idx][val].replace(/,/gi, "_"));
        }
      })
      .join(",");
  }

  private saveAsCsv(csvString: string): void {
    const blob: Blob = new Blob([csvString], { type: "text/csv;charset=utf8;" });
    saveAs(blob, "onsip-directory.csv");
  }

  openDownloadModal(): void {
    const dialog = this.dialog.open(DownloadDirectoryModalComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"],
      disableClose: true,
      autoFocus: false
    });

    this.unsubscriber.add(
      dialog.afterClosed().subscribe(data => {
        if (data === FileType.Pdf) {
          this.generatePDF();
        }
        if (data === FileType.Csv) {
          this.downloadCSV();
        }
      })
    );
  }

  private getExtensions(
    userAliases: Array<UserAlias>,
    addressSummary: AddressSummary
  ): Array<string> {
    const data = Object.values(userAliases)
      .filter(extension => extension.addressId === addressSummary.addressId)
      .map(filteredExtension => filteredExtension.aliasUsername);
    if (data.length) {
      return data;
    } else {
      return [" "];
    }
  }

  private getPhoneNumbers(dids: Array<Did>, addressSummary: AddressSummary) {
    const data = dids
      .filter(did => did?.userNumberAlias?.addressUsername === addressSummary.username)
      .map(phone => phone.number);
    if (data.length) {
      return data;
    } else {
      return [" "];
    }
  }

  protected setDataFilter(): void {
    this.dataSource.filterPredicate = (data, filter) => {
      const filterValue = filter.trim().toLowerCase();
      // sip address
      const sipAddress = data.sipAddress;
      // name
      const name = data.name;
      // extensions
      const ext = this.formatExtensionsPipe.transform(data.extensions).join(", ");
      // phone numbers
      const phoneNumbers =
        data.phoneNumbers?.map(phone => this.formatE164Pipe.transform(phone))?.join(", ") || "";
      // address type
      const addressType = data.addressType;

      return (
        sipAddress.toLowerCase().includes(filterValue) ||
        name.toLowerCase().includes(filterValue) ||
        ext.toLowerCase().includes(filterValue) ||
        phoneNumbers.toLowerCase().includes(filterValue) ||
        addressType.toLowerCase().includes(filterValue)
      );
    };
  }
}
